import { defineComponent as _defineComponent } from 'vue'
import ScaleOut from "@/components/scale-out-component.vue";
import BreadCrumbNavigation from "@/components/bread-crumb-component.vue";
import DocumentContent from "@/components/document-content.vue";
import { addSeconds, format } from "date-fns";
import { onMounted, ref, Ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import useHttpRequest from "@/composables/use-http-request";
import { useShowWarningMessage } from "@/composables/use-notifications";
import TextEditorComponent from "@/components/text-editor-component.vue";
import SubmitAnswerSheetDialog from "@/components/dialogs/submit-answer-sheet-dialog.vue";
import EditorGetChangesErrorDialog from "@/components/dialogs/editor-get-changes-error-dialog.vue";
import { Editor } from "tinymce";
import CourseWork from "@/interfaces/courseWork.interface";
import { DraftStorage } from "@/helpers/onlineAnswerSheet/draftStorage";
import { useManageStudentChanges } from "@/composables/course-work/use-manage-student-changes";
import { useAssessmentCountDown } from "@/composables/assessments/use-assessment-countdown";
import { useOnlineAnswersheetAttachments } from "@/composables/online-answersheet/use-online-answersheet-attachments";
import CourseWorkAttachmentsDialog from "@/components/dialogs/course-work-attachments-dialog.vue";
import { useSubmitCourseWork } from "@/composables/course-work/use-submit-course-work";
import useDownloadFile from "@/composables/use-download-file";


export default /*#__PURE__*/_defineComponent({
  __name: 'ongoing-course-work',
  setup(__props) {

const IS_WORKERS_SUPPORTED = typeof Worker !== "undefined";

const route = useRoute();
const router = useRouter();

const isLoading: Ref<boolean> = ref(false);
const activeView: Ref<"questionPaper" | "answerSheet"> = ref("questionPaper");
const submissionType = ref<"Online" | "File Upload">();
const courseWork: Ref<CourseWork | null> = ref(null);
const courseWorkId = ref(route.params.courseWorkId);

let courseWorkEndsAtEpochTimeInSecs = ref(0);

let textEditorInstance: Ref<Editor | undefined> = ref();
let draftStorage: Ref<DraftStorage | undefined> = ref(undefined);

let lastSavedAt: Ref<number | undefined> = ref();

const isUploadAnswerSheetDialogVisible = ref(false);

//Manage Course work Count Down clock
const { beginAssessmentTimer } = useAssessmentCountDown({
  endsAtEpochTimeInSecs: courseWorkEndsAtEpochTimeInSecs,
});

const {
  isOnlineAnswersheetAttachmentsDialogVisible,
  attachments,
  onAttachmentRemoved,
  onAttachmentAdded,
} = useOnlineAnswersheetAttachments();

//Manage getting and saving student changes
const {
  saveStudentChanges,
  saveStudentFinalChanges,
  onEditorSaveChangesFailed,
  showEditorContentSaveErrorDialog,
  onChangesFailedRedirectTo,
} = useManageStudentChanges({
  draftStorage,
  courseWork,
  textEditorInstance,
});

//Manage answer sheet submission
const {
  onSubmitFileUploadAnswerSheet,
  onSubmitOnlineAnswerSheet,
  onSubmitAnswerSheetIntent,
} = useSubmitCourseWork({
  courseWork,
  saveStudentChanges,
  saveStudentFinalChanges,
  isUploadAnswerSheetDialogVisible,
  submissionType,
  draftStorage,
});

const onTextEditorReady = (editor: Editor) => {
  textEditorInstance.value = editor;
};

const getCourseWork = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    course_work: CourseWork;
  }>({
    method: "GET",
    url: `course-work/ongoing-course-work/${
      courseWorkId.value
    }?timestamp=${new Date().getTime()}`,
    loadingProperty: isLoading,
    showSuccessMessage: false,
    errorLoadingProperty: null,
    showFullScreenLoader: false,
  });

  if (!request) {
    return router.back();
  }

  if (
    request.success &&
    request.message == "ONGOING COURSE WORK FETCHED SUCCESSFULLY"
  ) {
    courseWork.value = request.course_work;
    courseWorkEndsAtEpochTimeInSecs.value = +addSeconds(
      new Date(),
      request.course_work.ends_in_seconds
    );

    const submission = courseWork.value.student_course_work_submissions?.[0];

    if (submission) {
      attachments.value = submission.attachments.map((attachment) => {
        return {
          id: attachment.uuid,
          title: attachment.title,
          file_path: attachment.file_path_url,
          file_size: attachment.file_size,
        };
      });
    }

    if (submission && submission.special_submission_ends_in_seconds) {
      if (submission.special_submission_ends_in_seconds) {
        courseWorkEndsAtEpochTimeInSecs.value = +addSeconds(
          new Date(),
          submission.special_submission_ends_in_seconds
        );
      }
    }

    //If we have the aws_online_answersheet_path, means there could be some info in online submissions for the user
    const DRAFT_KEY = `draft-cw-${submission?.uuid}`;
    const DRAFT_TIMESTAMP_KEY = `${DRAFT_KEY}-updated`;

    draftStorage.value = new DraftStorage({
      draftKey: DRAFT_KEY,
      draftTimestampKey: DRAFT_TIMESTAMP_KEY,
    });

    beginAssessmentTimer();

    //If the Course work is not available for taking yet, then dont accept them to even view this page at the moment, just redirect them to Course works UI
  } else {
    return router.back();
  }
};

const handleClick = async (command: string) => {
  if (command == "AnswerSheet") {
    const submission = courseWork.value?.student_course_work_submissions?.[0];

    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      path_url: string;
    }>({
      method: "GET",
      url: `course-work/request-answersheet-word-document?student_course_work_submission_id=${
        submission?.student_course_work_submission_id
      }&timestamp=${new Date().getTime()}`,
      loadingProperty: null,
      showSuccessMessage: true,
      errorLoadingProperty: null,
      showFullScreenLoader: true,
    });

    if (!request) {
      return;
    }

    if (
      request.success &&
      request.message === "Answer sheet document fetched successfully"
    ) {
      useDownloadFile(request.path_url);
    }
  } else if (command == "QuestionPaper") {
    if (!courseWork.value?.question_paper_path_url) {
      return useShowWarningMessage(
        "No Question Paper",
        "There's No Question Paper attached. Please contact your lecturer"
      );
    }

    useDownloadFile(courseWork.value.question_paper_path_url, "Question Paper");
  }
};

const onEditorValueChange = async (content: string): Promise<void> => {
  // Save the content to local storage
  const saved = await draftStorage.value?.saveAsync(content);

  if (saved) {
    lastSavedAt.value = Date.now();
    // textEditorInstance.value?.notificationManager.open({
    //   text: "Changes saved locally",
    //   type: "info",
    //   timeout: 3000,
    // });
  }
};

onMounted(() => {
  getCourseWork();
});

return { __sfc: true,IS_WORKERS_SUPPORTED, route, router, isLoading, activeView, submissionType, courseWork, courseWorkId, courseWorkEndsAtEpochTimeInSecs, textEditorInstance, draftStorage, lastSavedAt, isUploadAnswerSheetDialogVisible, beginAssessmentTimer, isOnlineAnswersheetAttachmentsDialogVisible, attachments, onAttachmentRemoved, onAttachmentAdded, saveStudentChanges, saveStudentFinalChanges, onEditorSaveChangesFailed, showEditorContentSaveErrorDialog, onChangesFailedRedirectTo, onSubmitFileUploadAnswerSheet, onSubmitOnlineAnswerSheet, onSubmitAnswerSheetIntent, onTextEditorReady, getCourseWork, handleClick, onEditorValueChange, ScaleOut, BreadCrumbNavigation, DocumentContent, format, TextEditorComponent, SubmitAnswerSheetDialog, EditorGetChangesErrorDialog, CourseWorkAttachmentsDialog }
}

})